import { action, computed, observable } from 'mobx';
import { observer } from 'mobx-react';
import { autoinject, bind, CardItem, inject, ItemCarousel, StatusCard, toArray } from 'Mri.Web.Common';
import * as React from 'react';
import { AppStore } from '../../../Core/AppStore';
import { ActiveDisplay } from '../../ActiveDisplay';
import { DisplayScreenSnapshot } from '../../DisplayScreenSnapshot';
import { DisplaysStore } from '../../DisplaysStore';
import { DisplayBrightnessCard } from './DisplayBrightnessCard';
import { DisplayContractCard } from './DisplayContractCard';
import { DisplayDetailsCard } from './DisplayDetailsCard';
import { DisplayConnectionCard } from './DisplayConnectionCard';
import { DisplayEventProfilesCard } from './DisplayEventProfilesCard';
import { DisplayEventsCard } from './DisplayEventsCard';
import { DisplayMapCard } from './DisplayMapCard';
import { DisplayPartsCard } from './DisplayPartsCard';
import { DisplayPowerCard } from './DisplayPowerCard';
import { DisplayPowerInletCard } from './DisplayPowerInletCard';
import { DisplayMeasurmentsCard } from './DisplayMeasurementsCard';
import { DisplaySoftwareVersionsCard } from './DisplaySoftwareVersionsCard';
import { DisplaySolarCard } from './DisplaySolarCard';
import { DisplayVideoPathCard } from './DisplayVideoPathCard';
import { DisplayWeatherCard } from './DisplayWeatherCard';
import { InfiniteTouchCard } from './InfiniteTouchCard';
import { TicketsCard } from './TicketsCard';
import { UnitsStore } from '../../../Units/UnitsStore';
import { DisplayUnitCard } from './DisplayUnitCard';
import { DisplayGroupsCard } from './DisplayGroupsCard';
import { AlertActionsStore } from '../../../DisplayAlerts/AlertActionsStore';
import { DisplayServiceKitsCard } from './DisplayServiceKitsCard';
export interface IDisplayStatsCardsProps {
    readonly display: ActiveDisplay;
    readonly loading: boolean;
    readonly history: any;
}

@autoinject
@observer
export class DisplayStatsCards extends React.Component<IDisplayStatsCardsProps> {
    @action
    public async componentDidMount() {
        this.actionStoreLoading = true;
        this.loadAllAsync();
    }

    public render() {
        const { display, loading } = this.props;

        return (
            <>
                {this.screenSnapshots && this.screenSnapshots?.length > 0 && (
                    <div className={this.cardClass + ' text-center'}>
                        <StatusCard title={`PopVu™`} loading={this.props.loading}>
                            <div className="d-flex align-items-center justify-content-evenly h-100 p-2">
                                {this.screenSnapshots &&
                                    this.screenSnapshots.map(s => (
                                        <DisplayScreenSnapshot
                                            key={s.uri}
                                            screenSnapshot={s}
                                            className="d-inline-block"
                                        />
                                    ))}
                            </div>
                        </StatusCard>
                    </div>
                )}

                {this.infiniteTouchSnapshots && this.infiniteTouchSnapshots?.length > 0 && (
                    <div className={this.cardClass + ' text-center'}>
                        <StatusCard title={`InfiniteTouch®`} loading={this.props.loading}>
                            <div className="d-flex align-items-center justify-content-evenly h-100 p-2">
                                {this.infiniteTouchSnapshots &&
                                    this.infiniteTouchSnapshots.map(s => (
                                        <DisplayScreenSnapshot
                                            key={s.uri}
                                            screenSnapshot={s}
                                            className="d-inline-block"
                                        />
                                    ))}
                            </div>
                        </StatusCard>
                    </div>
                )}

                {this.unit && this.unit.displays.length > 1 && (
                    <div className={this.cardClass}>
                        <DisplayUnitCard display={display} unit={this.unit} loading={this.props.loading} />
                    </div>
                )}

                <div className={this.cardClass}>
                    <TicketsCard display={display} loading={this.props.loading} />
                </div>

                <div className={this.cardClass}>
                    <DisplayMeasurmentsCard statusPoll={this.statusPoll} loading={this.props.loading} />
                </div>

                <div className={this.cardClass}>
                    <DisplayEventsCard display={display} history={this.props.history} />
                </div>

                <div className={this.cardClass}>
                    <DisplayDetailsCard display={display} loading={this.props.loading} statusPoll={this.statusPoll} />
                </div>

                <div className={this.cardClass}>
                    <DisplayConnectionCard
                        display={display}
                        loading={this.props.loading}
                        statusPoll={this.statusPoll}
                    />
                </div>

                <div className={this.cardClass}>
                    <DisplayBrightnessCard statusPoll={this.statusPoll} loading={this.props.loading} />
                </div>

                {this.statusPoll?.videoPath && (
                    <div className={this.cardClass}>
                        <DisplayVideoPathCard videoPath={this.statusPoll.videoPath} loading={this.props.loading} />
                    </div>
                )}

                <div className={this.cardClass}>
                    <DisplayPowerInletCard telemetryPoll={this.telemetryPoll} loading={this.props.loading} />
                </div>

                <div className={this.cardClass}>
                    <DisplayPowerCard
                        statusPoll={this.statusPoll}
                        telemetryPoll={this.telemetryPoll}
                        loading={this.props.loading}
                    />
                </div>

                <div className={this.cardClass}>
                    <StatusCard title="Fan Banks" loading={this.props.loading}>
                        {this.statusPoll?.fanBanks?.map((f, idx) => (
                            <CardItem
                                key={idx}
                                label={f.name}
                                value={f.avgTachSpeed ? `${f.avgTachSpeed} RPM @ ${f.pwm}%` : '-'}
                            />
                        ))}
                    </StatusCard>
                </div>

                <div className={this.cardClass}>
                    <DisplayContractCard display={display} />
                </div>

                {this.showMap && (
                    <div className={this.cardClass}>
                        <DisplayMapCard display={display} loading={loading} />
                    </div>
                )}

                <div className={this.cardClass}>
                    <DisplaySoftwareVersionsCard display={display} />
                </div>

                <div className={this.cardClass}>
                    <DisplayPartsCard display={display} />
                </div>
                <div className={this.cardClass}>
                    <DisplayServiceKitsCard display={display} />
                </div>

                {this.showEventProfiles && !this.actionStoreLoading && (
                    <div className={this.cardClass}>
                        <DisplayEventProfilesCard
                            eventProfiles={this.eventProfiles}
                            alertActionsStore={this.alertActionsStore}
                        />
                    </div>
                )}

                {this.hasSolarPanel && (
                    <div className={this.cardClass}>
                        <DisplaySolarCard loading={loading} telemetryPoll={this.telemetryPoll} />
                    </div>
                )}

                {this.statusPoll?.sigmaSense && (
                    <div className={this.cardClass}>
                        <InfiniteTouchCard statusPoll={this.statusPoll} loading={loading} />
                    </div>
                )}

                {this.statusPoll?.weatherVuStatus && (
                    <div className={this.cardClass}>
                        <DisplayWeatherCard
                            display={display}
                            statusPoll={this.statusPoll}
                            loading={this.props.loading}
                        />
                    </div>
                )}

                {this.props.display.displayGroups && (
                    <div className={this.cardClass}>
                        <DisplayGroupsCard display={display} />
                    </div>
                )}
            </>
        );
    }

    @bind
    @action
    private async loadAllAsync() {
        this.actionStoreLoading = true;
        try {
            await this.alertActionsStore.loadAllAsync();
            await this.alertActionsStore.loadRecipientsAsync();
        } finally {
            this.actionStoreLoading = false;
        }
    }

    @computed
    private get cardClass() {
        return 'col-md-6 mb-3 px-2';
    }

    @computed
    private get eventProfiles() {
        return this.displaysStore.profiles.get(this.props.display.serialNumber);
    }

    @computed
    private get showEventProfiles() {
        return !!this.eventProfiles?.length;
    }

    @computed
    private get screenSnapshots() {
        const snapshots = this.displaysStore.screenSnapshots.get(this.props.display.serialNumber);
        return snapshots?.filter(s => s.captureType == Mri.ScreenSnapshots.CaptureType.Snapshot);
    }

    @computed
    private get infiniteTouchSnapshots() {
        const snapshots = this.displaysStore.screenSnapshots.get(this.props.display.serialNumber);
        return snapshots?.filter(s => s.captureType == Mri.ScreenSnapshots.CaptureType.TouchHeatmap);
    }

    @computed
    private get activeDisplayStatus() {
        return this.displaysStore.statuses.get(this.props.display.serialNumber);
    }

    @computed
    private get statusPoll() {
        if (this.activeDisplayStatus) return this.activeDisplayStatus.lastKnownPoll.displayStatusPoll;
    }

    @computed
    private get telemetryPoll() {
        return this.displaysStore.telemetryPolls.get(this.props.display.serialNumber);
    }

    @computed
    private get showMap() {
        const { display } = this.props;

        return (
            !!display && !!display.unitLocation && !!display.unitLocation.latitude && !!display.unitLocation.longitude
        );
    }

    @computed
    private get hasSolarPanel() {
        if (this.telemetryPoll)
            return this.telemetryPoll.measurements.find(
                s => s.id == Mri.WebApi.MeasurementId.TotalSolarBatteryChargeAmps
            )
                ? true
                : false;
    }

    @computed
    private get unit() {
        return this.unitsStore.units.get(this.props.display.unitSerialNumber);
    }

    @inject
    private appStore!: AppStore;
    @inject
    private displaysStore!: DisplaysStore;
    @inject
    private alertActionsStore!: AlertActionsStore;
    @inject
    private unitsStore!: UnitsStore;

    @observable
    private actionStoreLoading: boolean = false;
}
